import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Calendar, Phone, Link, User, Building } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/formatters";
import { BannerImageUpload } from "./BannerImageUpload";

const campaignSchema = z.object({
  campaign_name: z.string().min(1, "Campaign name is required"),
  campaign_url: z.string().url("Must be a valid URL").refine(
    (url) => url.startsWith("http://") || url.startsWith("https://"),
    "URL must start with http:// or https://"
  ),
  campaign_period_days: z.number().min(1, "Period must be at least 1 day").max(365, "Period cannot exceed 365 days"),
  client_name: z.string().min(1, "Client name is required"),
  client_contact: z.string().min(1, "Client contact is required"),
  phone_number_1: z.string().min(1, "Phone number 1 is required"),
  phone_number_2: z.string().min(1, "Phone number 2 is required"),
  banner_image_url: z.string().min(1, "Banner image is required"),
});

type CampaignFormData = z.infer<typeof campaignSchema>;

interface Campaign {
  id: string;
  campaign_name: string;
  campaign_url: string;
  campaign_period_days: number;
  client_name: string;
  client_contact: string;
  phone_number_1: string;
  phone_number_2: string;
  is_active: boolean;
  start_date: string;
  end_date: string;
  created_at: string;
  banner_image_url?: string;
}

export function CampaignManagement() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bannerImageUrl, setBannerImageUrl] = useState<string>("");
  const { profile } = useAuth();
  const { toast } = useToast();

  const form = useForm<CampaignFormData>({
    resolver: zodResolver(campaignSchema),
  });

  const fetchCampaigns = async () => {
    try {
      // First expire old campaigns
      await supabase.rpc('expire_old_campaigns');
      
      const { data, error } = await supabase
        .from('campaigns')
        .select('*, banner_image_url')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast({
        title: "Error",
        description: "Failed to fetch campaigns",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: CampaignFormData) => {
    if (!profile?.id) return;

    // Check active campaigns count using database function
    try {
      const { data: countData, error: countError } = await supabase.rpc('get_active_campaigns_count');
      
      if (countError) throw countError;
      
      if (countData >= 4) {
        toast({
          title: "Maximum 4 campaigns allowed",
          description: "Please stop an existing campaign first.",
          variant: "destructive",
        });
        return;
      }
    } catch (error) {
      console.error('Error checking campaign count:', error);
      toast({
        title: "Error",
        description: "Failed to check campaign limit",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('campaigns')
        .insert({
          campaign_name: data.campaign_name,
          campaign_url: data.campaign_url,
          campaign_period_days: data.campaign_period_days,
          client_name: data.client_name,
          client_contact: data.client_contact,
          phone_number_1: data.phone_number_1,
          phone_number_2: data.phone_number_2,
          banner_image_url: data.banner_image_url,
          created_by: profile.id,
          end_date: new Date(Date.now() + data.campaign_period_days * 24 * 60 * 60 * 1000).toISOString(),
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast({
            title: "Campaign Name Exists",
            description: "A campaign with this name already exists. Please choose a different name.",
            variant: "destructive",
          });
          return;
        }
        throw error;
      }

      toast({
        title: "Success",
        description: "Campaign created successfully",
      });
      
      form.reset();
      setBannerImageUrl("");
      fetchCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast({
        title: "Error",
        description: "Failed to create campaign",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCampaignStatus = async (campaignId: string, currentStatus: boolean) => {
    try {
      // If activating, check campaign limit first
      if (!currentStatus) {
        const { data: countData, error: countError } = await supabase.rpc('get_active_campaigns_count');
        
        if (countError) throw countError;
        
        if (countData >= 4) {
          toast({
            title: "Maximum 4 campaigns allowed",
            description: "Please stop an existing campaign first.",
            variant: "destructive",
          });
          return;
        }
      }

      const { error } = await supabase
        .from('campaigns')
        .update({ is_active: !currentStatus })
        .eq('id', campaignId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Campaign ${!currentStatus ? 'activated' : 'stopped'} successfully`,
      });
      
      fetchCampaigns();
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast({
        title: "Error",
        description: "Failed to update campaign status",
        variant: "destructive",
      });
    }
  };

  const getRemainingDays = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getStatusBadge = (campaign: Campaign) => {
    const remainingDays = getRemainingDays(campaign.end_date);
    const isExpired = remainingDays === 0;
    const isActive = campaign.is_active && !isExpired;

    if (isExpired) {
      return <Badge variant="secondary">Expired</Badge>;
    }
    if (isActive) {
      return <Badge variant="default">Active ({remainingDays} days left)</Badge>;
    }
    return <Badge variant="outline">Inactive</Badge>;
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader style={{ backgroundColor: 'orange' }}>
          <CardTitle>Commercial Banner Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">Loading campaigns...</div>
        </CardContent>
      </Card>
    );
  }

  const activeCampaignsCount = campaigns.filter(c => 
    c.is_active && new Date(c.end_date) > new Date()
  ).length;

  return (
    <Card>
      <CardHeader style={{ backgroundColor: 'orange' }}>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Commercial Banner Management
        </CardTitle>
        <CardDescription>
          Manage advertising campaigns and banner displays. 
          Active campaigns: {activeCampaignsCount}/4
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="list" className="w-full">
          <TabsList>
            <TabsTrigger value="list">Campaign List</TabsTrigger>
            <TabsTrigger value="create">Create Campaign</TabsTrigger>
          </TabsList>

          <TabsContent value="list" className="space-y-4">
            {campaigns.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No campaigns created yet
              </div>
            ) : (
              <div className="space-y-4">
                {campaigns.map((campaign) => (
                  <div key={campaign.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold">{campaign.campaign_name}</h3>
                        {getStatusBadge(campaign)}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleCampaignStatus(campaign.id, campaign.is_active)}
                        disabled={!campaign.is_active && activeCampaignsCount >= 4}
                      >
                        {campaign.is_active ? 'Stop Campaign' : 'Activate'}
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Link className="h-4 w-4" />
                        <a href={campaign.campaign_url} target="_blank" rel="noopener noreferrer" 
                           className="text-blue-600 hover:underline truncate">
                          {campaign.campaign_url}
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        <span>{campaign.client_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{campaign.client_contact}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <span>{campaign.phone_number_1}</span>
                      </div>
                    </div>
                    
                     {campaign.banner_image_url && (
                       <div className="mt-2">
                         <img 
                           src={campaign.banner_image_url} 
                           alt={campaign.campaign_name}
                           className="w-full max-w-[364px] h-[45px] object-cover border rounded"
                         />
                       </div>
                     )}
                     
                     <div className="text-xs text-muted-foreground">
                       Period: {formatDate(campaign.start_date)} - {formatDate(campaign.end_date)} 
                       ({campaign.campaign_period_days} days)
                     </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="create">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="campaign_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Campaign Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter unique campaign name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="campaign_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Campaign URL *</FormLabel>
                        <FormControl>
                          <Input placeholder="https://example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="campaign_period_days"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Campaign Period (Days) *</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            placeholder="30" 
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="client_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter client name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="client_contact"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client Contact *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter contact person name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone_number_1"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number 1 *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter primary phone number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                   <FormField
                     control={form.control}
                     name="phone_number_2"
                     render={({ field }) => (
                       <FormItem>
                         <FormLabel>Phone Number 2 *</FormLabel>
                         <FormControl>
                           <Input placeholder="Enter secondary phone number" {...field} />
                         </FormControl>
                         <FormMessage />
                       </FormItem>
                     )}
                   />
                 </div>

                 <FormField
                   control={form.control}
                   name="banner_image_url"
                   render={({ field }) => (
                     <FormItem>
                       <FormControl>
                         <BannerImageUpload
                           onImageUploaded={(url) => {
                             setBannerImageUrl(url);
                             field.onChange(url);
                           }}
                           currentImageUrl={bannerImageUrl}
                           onImageRemoved={() => {
                             setBannerImageUrl("");
                             field.onChange("");
                           }}
                         />
                       </FormControl>
                       <FormMessage />
                     </FormItem>
                   )}
                 />

                <div className="flex justify-end">
                  <Button type="submit" disabled={submitting || activeCampaignsCount >= 4}>
                    {submitting ? "Creating..." : "Create Campaign"}
                  </Button>
                </div>
                
                {activeCampaignsCount >= 4 && (
                  <p className="text-sm text-amber-600">
                    Maximum campaign limit reached (4/4). Deactivate a campaign to create a new one.
                  </p>
                )}
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}